import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, User, Globe, Shield, Heart, MessageCircle, Star, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, isAuthenticated, loading, signUp, signIn } = useSupabaseAuth();
  
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Redirect if fully authenticated
  React.useEffect(() => {
    if (isAuthenticated && user && profile) {
      console.log('User is authenticated, redirecting to home');
      navigate('/');
    }
  }, [isAuthenticated, user, profile, navigate]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (authMode === 'signup') {
      if (!formData.firstName || formData.firstName.trim().length < 2) {
        newErrors.firstName = 'First name must be at least 2 characters';
      }
      if (!formData.lastName || formData.lastName.trim().length < 2) {
        newErrors.lastName = 'Last name must be at least 2 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsAuthenticating(true);
    setErrors({});

    try {
      if (authMode === 'signup') {
        console.log('Attempting signup for:', formData.email);
        
        const { user: newUser, error } = await signUp(
          formData.email,
          formData.password,
          formData.firstName,
          formData.lastName
        );

        if (error) {
          console.error('Signup error:', error);
          // Handle specific error types for signup
          if (error.message.includes('compromised') || error.message.includes('breach')) {
            setErrors({ 
              password: 'This password has been found in a data breach. Please choose a different password for your security.' 
            });
          } else if (error.message.includes('Password should be at least')) {
            setErrors({ password: 'Password must be at least 6 characters long.' });
          } else if (error.message.includes('User already registered')) {
            setErrors({ email: 'An account with this email already exists. Switching to sign in...' });
            // Automatically switch to sign in mode
            setTimeout(() => {
              setAuthMode('signin');
              setErrors({});
            }, 2000);
          } else {
            setErrors({ general: error.message });
          }
        } else if (newUser) {
          console.log('Signup successful');
          alert('Account created successfully! Welcome to πPi Dating! 🎉');
          // Don't navigate immediately, let the auth state change handle it
        }
      } else {
        console.log('Attempting signin for:', formData.email);
        
        const { user: signedInUser, error } = await signIn(formData.email, formData.password);

        if (error) {
          console.error('Signin error:', error);
          // Handle specific error types
          if (error.message.includes('compromised') || error.message.includes('breach')) {
            setErrors({ 
              password: 'This password has been found in a data breach. Please choose a different password for your security.' 
            });
          } else if (error.message.includes('Invalid login credentials')) {
            setErrors({ general: 'Invalid email or password. Please check your credentials and try again.' });
          } else {
            setErrors({ general: error.message });
          }
        } else if (signedInUser) {
          console.log('Signin successful');
          // Don't navigate immediately, let the auth state change handle it
        }
      }
    } catch (error: any) {
      console.error('Authentication error:', error);
      setErrors({ general: error.message || 'Authentication failed' });
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading πPi Dating...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/logo 3 BR.png" alt="Pi Dating" className="h-16 w-16" />
            <img src="/unnamed (1).png" alt="Pi Network" className="h-16 w-16" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            πPi Dating
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Pioneer Dating App - Connect & Find Love
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          <div className="p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg text-center">
            <Heart className="text-purple-600 dark:text-purple-400 mx-auto mb-1" size={20} />
            <p className="text-xs text-purple-700 dark:text-purple-300">Find Matches</p>
          </div>
          <div className="p-3 bg-pink-50 dark:bg-pink-900/30 rounded-lg text-center">
            <MessageCircle className="text-pink-600 dark:text-pink-400 mx-auto mb-1" size={20} />
            <p className="text-xs text-pink-700 dark:text-pink-300">Chat Securely</p>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-center">
            <Zap className="text-blue-600 dark:text-blue-400 mx-auto mb-1" size={20} />
            <p className="text-xs text-blue-700 dark:text-blue-300">Pi Payments</p>
          </div>
          <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg text-center">
            <Shield className="text-green-600 dark:text-green-400 mx-auto mb-1" size={20} />
            <p className="text-xs text-green-700 dark:text-green-300">Verified Users</p>
          </div>
        </div>

        {/* Authentication Tabs */}
        <div className="flex mb-6 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setAuthMode('signin')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              authMode === 'signin'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            Sign In
          </button>
          <button
            onClick={() => setAuthMode('signup')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              authMode === 'signup'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            Sign Up
          </button>
        </div>

        {/* Email/Password Authentication */}
        <form onSubmit={handleEmailAuth} className="space-y-4">
          {/* General Error */}
          {errors.general && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
              <p className="text-red-700 dark:text-red-300 text-sm">{errors.general}</p>
            </div>
          )}

          {/* Name Fields (Signup only) */}
          {authMode === 'signup' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="First name"
                />
                {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="Last name"
                />
                {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
              </div>
            </div>
          )}

          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <div className="relative">
              <Mail size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                  errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Enter your email"
              />
            </div>
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className={`w-full pl-10 pr-12 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                  errors.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={authMode === 'signup' ? 'Choose a strong, unique password' : 'Enter your password'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            {authMode === 'signup' && !errors.password && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                We check passwords against known data breaches for your security
              </p>
            )}
          </div>

          {/* Confirm Password (Signup only) */}
          {authMode === 'signup' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="Confirm your password"
                />
              </div>
              {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isAuthenticating}
            variant="gradient"
            size="lg"
            className="w-full"
          >
            {isAuthenticating ? (
              <>
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                {authMode === 'signup' ? 'Creating Account...' : 'Signing In...'}
              </>
            ) : (
              <>
                <User size={20} className="mr-2" />
                {authMode === 'signup' ? 'Create Account' : 'Sign In'}
              </>
            )}
          </Button>
        </form>

        {/* App Benefits */}
        <Card className="p-4 mt-6">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">
            Why Choose πPi Dating?
          </h3>
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
            <div className="flex items-center gap-2">
              <Star size={16} className="text-yellow-500" />
              <span>Connect with verified users</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-green-500" />
              <span>Secure authentication & privacy</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap size={16} className="text-purple-500" />
              <span>Optional Pi Network integration</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart size={16} className="text-pink-500" />
              <span>Find meaningful connections</span>
            </div>
          </div>
        </Card>

        {/* Footer */}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-6 text-center">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </Card>
    </div>
  );
};

export default LoginPage;